'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useTekstClient } from 'hooks/useTekstClient';
import styles from './AddOnSearchBar.module.scss';

/**
 * Search bar for add-ons page. Searches by add-on name or description.
 * Uses URL search params for server-side filtering.
 * Optional filter toggle button hides/shows the filters sidebar.
 */
export default function AddOnSearchBar({ filtersVisible = true, onFiltersToggle, hasFilters = false }) {
    const { tekst } = useTekstClient();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [value, setValue] = useState(searchParams.get('search') ?? '');

    useEffect(() => {
        setValue(searchParams.get('search') ?? '');
    }, [searchParams]);

    const updateSearch = (searchValue) => {
        const params = new URLSearchParams(searchParams.toString());
        if (searchValue.trim()) {
            params.set('search', searchValue.trim());
        } else {
            params.delete('search');
        }
        params.set('page', '1');
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateSearch(value);
    };

    const handleChange = (e) => {
        const newValue = e.target.value;
        setValue(newValue);
        if (!newValue && searchParams.has('search')) {
            updateSearch('');
        }
    };

    const filterToggleLabel = filtersVisible
        ? tekst('mkgeu:mkgaddons:search:toggle:hidefilters')
        : tekst('mkgeu:mkgaddons:search:toggle:showfilters');

    return (
        <form onSubmit={handleSubmit} className={styles.searchForm}>
            <div className={styles.searchWrapper}>
                {hasFilters && (
                    <button
                        type="button"
                        className={styles.filterToggleBtn}
                        onClick={onFiltersToggle}
                        aria-label={filterToggleLabel}
                        title={filterToggleLabel}
                    >
                        <Image
                            src={filtersVisible ? 'https://share.mkg.eu/web/FilterAddOnHide.svg' : 'https://share.mkg.eu/web/FilterAddOnShow.svg'}
                            alt=""
                            width={27}
                            height={28}
                            className={styles.filterToggleIcon}
                        />
                    </button>
                )}

                <input
                    type="search"
                    value={value}
                    onChange={handleChange}
                    placeholder={tekst('mkgeu:mkgaddons:search:placeholder')}
                    className={styles.searchInput}
                    aria-label={tekst('mkgeu:mkgaddons:search:arialabel')}
                />
                <button type="submit" className={`btn-nostyling ${styles.searchButton}`}>
                    {tekst('mkgeu:mkgaddons:search:button')}
                </button>
            </div>
        </form>
    );
}
